/**
 * 그리퍼 정규화 출력 → 3D 손가락 벌림.
 *
 * 사용자 결정(2026-11-02): 출력은 원래 Sigmakit 규약대로 0 = 완전히 열림, 1 = 닫힘이다.
 * 손가락 벌림 = 1 − 출력이고 [0, 1] 로 자른다. 근거는 Sigmakit 실기 녹화다
 * (명령은 episode 시작 · 끝에서 0, 쥘 때 1). 입력 끝점을 거꾸로 적은 매핑은 손가락도 거꾸로 움직인다.
 *
 * Owns:
 * - 출력값 → 벌림 비율, 재생 시각의 벌림 조회(표본 사이를 보간하지 않는다).
 *
 * Does not own:
 * - 정규화 식 — `mappingPose.ts`. 관절 각 — `sim/handGlyph.ts`.
 */
import type { GripperOpening } from "../domain/viewerTypes";
import type { GripperSeries } from "./mappingPose";
import { findTimeSpan } from "./poseMath";
import { preparePoseSeries, samplePoseAt } from "./poseSampling";
import type { PoseSeries } from "../domain/viewerTypes";

const timelines = new WeakMap<GripperOpening, PoseSeries>();

/** 그리퍼 값이 없을 때 그리는 벌림. Sigmakit 이 쉬는 자세(열림)다. 표시용이고 값이 아니다. */
export const RESTING_OPENING = 1;

export function gripperOpening(series: GripperSeries): GripperOpening {
  return {
    timestampsSec: series.timestampsSec,
    opening: series.values.map((value) =>
      value === null ? null : 1 - Math.min(Math.max(value, 0), 1),
    ),
  };
}

/**
 * `seconds` 이하의 마지막 유효 벌림 값을 쓴다. 시간 공백·범위 밖·잘못된 행은 `null` 이다.
 * 호출자는 `null`일 때 G를 비우고, 손은 Sigmakit 이 쉬는 자세(열림)로 그린다. 그 모양은 표시용이다.
 */
export function openingAt(series: GripperOpening, seconds: number): number | null {
  const { timestampsSec } = series;
  let timeline = timelines.get(series);
  if (timeline === undefined) {
    timeline = preparePoseSeries(
      timestampsSec,
      series.opening.map((value) =>
        value === null || !Number.isFinite(value) ? null : [0, 0, 0, 1, 0, 0, 0],
      ),
    );
    timelines.set(series, timeline);
  }
  if (samplePoseAt(timeline, seconds).pose === null) {
    return null;
  }
  const { hi, lo } = findTimeSpan(timestampsSec, seconds);
  const row = timestampsSec[hi] <= seconds ? hi : lo;
  return series.opening[row] ?? null;
}
